import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { Package, Truck, CheckCircle2, Boxes, ArrowUpRight } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { ProfitOverview } from "@/components/ProfitOverview";

const KPI = ({ label, value, sub, icon: Icon, testid }) => (
  <div className="card p-6 fade-in" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="label-eyebrow">{label}</div>
      <Icon size={16} strokeWidth={1.5} className="text-[var(--text-3)]" />
    </div>
    <div className="mt-4 text-4xl font-mono font-semibold tracking-tight text-[var(--text)]" data-testid={`${testid}-value`}>{value}</div>
    {sub && <div className="text-xs text-[var(--text-3)] mt-2">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, o] = await Promise.all([
          api.get("/stats/dashboard"),
          api.get("/orders"),
        ]);
        setStats(s.data);
        setRecent(o.data.slice(0, 8));
      } catch (e) { /* eslint-disable-line */ }
    })();
  }, []);

  const isCustomer = user?.role === "customer";

  // Personalised greeting per role (Schweizerisch-warm)
  const firstName = user?.name?.split(" ")[0] || "";
  const role = user?.role;
  const hour = new Date().getHours();
  const greeting =
    role === "admin" || role === "dispatcher"
      ? `Hi ${firstName} 👋`
      : role === "driver"
      ? `Servus ${firstName} – gueti Tour! 🚚`
      : role === "customer"
      ? (hour < 11 ? `Guete Morge, ${firstName} Team 👋` : `Hoi zäme, ${firstName} Team 👋`)
      : `Hoi ${firstName} 👋`;

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>
              {isCustomer ? "Kundenportal" : "Kontrollzentrum"}
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">
              {greeting}
            </h1>
            <p className="text-sm text-[var(--text-3)] mt-1">
              {new Date().toLocaleDateString("de-CH", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
            </p>
          </div>
          {isCustomer && (
            <button onClick={() => navigate("/portal/orders/new")} data-testid="dashboard-create-order-btn" className="btn-gold px-5 py-3 rounded-sm text-sm uppercase tracking-wider">
              + Neuer Auftrag
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 stagger" data-testid="dashboard-kpis">
          <KPI label="Aufträge gesamt" value={stats?.orders_total ?? "—"} icon={Package} testid="kpi-total" />
          <KPI label="Offen" value={stats?.open_orders ?? "—"} icon={Boxes} testid="kpi-open" />
          <KPI label="Unterwegs" value={stats?.in_transit ?? "—"} icon={Truck} testid="kpi-transit" />
          <KPI label="Zugestellt" value={stats?.delivered ?? "—"} icon={CheckCircle2} testid="kpi-delivered" />
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <KPI label="Paletten / Monat" value={stats?.pallets_month ?? "—"} icon={Boxes} testid="kpi-pallets-month" />
          {!isCustomer && (
            <KPI label="Umsatz Monat" value={stats?.revenue_chf_month ? `CHF ${Number(stats.revenue_chf_month).toLocaleString("de-CH")}` : "—"} icon={ArrowUpRight} testid="kpi-revenue" />
          )}
        </div>

        {/* Umsatz & Gewinn — nur Admin */}
        {user?.role === "admin" && <ProfitOverview />}

        {/* Recent Orders */}
        <div className="mt-10">
          <div className="flex items-end justify-between mb-4">
            <h2 className="text-xl font-semibold">Letzte Aufträge</h2>
            <button
              onClick={() => navigate(isCustomer ? "/portal/orders" : "/admin/orders")}
              data-testid="dashboard-view-all-orders"
              className="text-xs uppercase tracking-wider text-[var(--text-2)] hover:text-[var(--text)]"
            >
              Alle anzeigen →
            </button>
          </div>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                  <th className="text-left py-3 px-4">Auftrag</th>
                  {!isCustomer && <th className="text-left py-3 px-4">Kunde</th>}
                  <th className="text-left py-3 px-4">Lieferdatum</th>
                  <th className="text-left py-3 px-4">Paletten</th>
                  <th className="text-left py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-10 text-[var(--text-3)]" data-testid="dashboard-no-orders">Keine Aufträge vorhanden.</td></tr>
                )}
                {recent.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => navigate(isCustomer ? `/portal/orders/${o.id}` : `/admin/orders/${o.id}`)}
                    className="border-b border-[var(--border)] hover:bg-[var(--surface-2)] cursor-pointer transition-colors"
                    data-testid={`dashboard-order-row-${o.order_number}`}
                  >
                    <td className="py-3 px-4 font-mono text-[var(--text)]">{o.order_number}</td>
                    {!isCustomer && <td className="py-3 px-4 text-[var(--text)]">{o.customer_name || "—"}</td>}
                    <td className="py-3 px-4 text-[var(--text)]">{formatDate(o.delivery_date)}</td>
                    <td className="py-3 px-4 font-mono text-[var(--text)]">{o.pallet_count}</td>
                    <td className="py-3 px-4"><StatusBadge status={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
